/**
 * Work Order configuration helpers.
 *
 * The work order integration is configured per-organization and stored in the
 * existing `organization_configuration` table as a single JSON blob under the
 * `WorkOrderConfig` value_type. Keeping it in one row (rather than several
 * value_types) lets us treat it as one structured object.
 *
 * Phase 1 (this file) only uses { enabled, label, url } — a configurable link
 * to the org's external work order system. Additional fields (submitEndpoint,
 * hiddenFields, contact, ...) are reserved for the future programmatic-submit
 * phase and are intentionally preserved by the read/save round-trip.
 */
import Util from './util';

export const WORK_ORDER_VALUE_TYPE = 'WorkOrderConfig';

export const DEFAULT_WORK_ORDER_CONFIG = {
  enabled: false,
  label: 'Submit Work Order',
  url: '',
};

/**
 * Ensure a work order URL has a scheme so Linking.openURL treats it as an
 * external link. Admins commonly type a bare host (e.g. "example.com/path");
 * without a scheme it would be treated as an in-app/relative link and fail.
 * Prepends https:// when no scheme is present. Returns '' for empty input.
 * @param {string} raw
 * @returns {string}
 */
export const normalizeWorkOrderUrl = (raw) => {
  const url = (raw || '').trim();
  if (!url) return '';
  // Already has a scheme (https://, http://, etc.) — leave it alone.
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) return url;
  // Protocol-relative (//host) — default to https.
  if (url.startsWith('//')) return `https:${url}`;
  return `https://${url}`;
};

/**
 * Validate that a work order URL is a real http(s) URL with a proper host —
 * rejecting incomplete values like a lone "https://", a malformed "https:/",
 * or a bare token like "h". Normalizes first, so a bare host (e.g.
 * "example.com") is considered valid. The host must be a dotted domain,
 * an IP, or "localhost", optionally with a port and path.
 * @param {string} raw
 * @returns {boolean}
 */
export const isValidWorkOrderUrl = (raw) => {
  const url = normalizeWorkOrderUrl(raw);
  if (!url) return false;
  const host = '(localhost|[a-z0-9-]+(\\.[a-z0-9-]+)+)';
  const re = new RegExp(`^https?://${host}(:\\d+)?(/\\S*)?$`, 'i');
  return re.test(url);
};

const queryConfigRows = async (organization) => {
  const data = await Util.get('Resty', '/items/organization_configuration/value_type', {
    queryParams: { organization, value_type: WORK_ORDER_VALUE_TYPE },
  });
  return data?.items || [];
};

/**
 * Fetch the work order config for an organization, merged over defaults.
 * Returns the defaults (disabled) when nothing is configured.
 * @param {string} organization
 * @returns {Promise<object>}
 */
export const getWorkOrderConfig = async (organization) => {
  if (!organization) return { ...DEFAULT_WORK_ORDER_CONFIG };

  try {
    const rows = await queryConfigRows(organization);
    if (!rows.length) return { ...DEFAULT_WORK_ORDER_CONFIG };

    // Most recently written row wins (saves should leave only one, but be safe).
    const latest = rows.reduce((a, b) => (a.timestamp >= b.timestamp ? a : b));

    let parsed = {};
    try {
      parsed = JSON.parse(latest.value);
    } catch {
      parsed = {};
    }
    return { ...DEFAULT_WORK_ORDER_CONFIG, ...parsed };
  } catch (error) {
    Util.printAPIError(error);
    return { ...DEFAULT_WORK_ORDER_CONFIG };
  }
};

/**
 * Persist the work order config for an organization. Removes any existing
 * rows for this value_type first so a single authoritative row remains.
 * Admin-only — enforced server-side by the organization_configuration route.
 * @param {string} organization
 * @param {object} config
 */
export const saveWorkOrderConfig = async (organization, config) => {
  if (!organization) throw new Error('organization is required');

  const existing = await queryConfigRows(organization);
  for (const row of existing) {
    await Util.del('Resty', '/items/organization_configuration', {
      queryParams: { organization, timestamp: row.timestamp },
    });
  }

  await Util.post('Resty', '/items/organization_configuration', {
    queryParams: {
      organization,
      value_type: WORK_ORDER_VALUE_TYPE,
      value: JSON.stringify(config),
    },
  });
};
